/**
 * Per-CRM sync-status data hooks (Salesforce, HubSpot, and Attio and
 * Pipedrive behind `attio_crm_v1` / `pipedrive_v1`).
 *
 * Owns connected-CRM detection plus the batched status lookups that drive
 * the thin per-CRM columns on the lead-list table, People Search, and the
 * People/Companies directories. Chunking at the BE's 500-id limit lives in
 * the RTK endpoints (`getCrmSyncStatus` / `getCrmCompanySyncStatus`); these
 * hooks only dedupe/sort the key set so every surface shares one cache
 * entry per id set.
 *
 * Status fetch failures (including the companies endpoint 404ing while the
 * BE lands) degrade to `statusUnknown` - cells render a dim non-interactive
 * state and NO error toast is raised.
 */
import { useCallback, useMemo, useState } from 'react';
import { useOrganization } from '@clerk/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import {
  useGetCrmCompanySyncStatusQuery,
  useGetCrmOptionsQuery,
  useGetCrmStatusByIdentifierQuery,
  useGetCrmStatusQuery,
  useGetCrmSyncStatusQuery,
  useLazyGetCrmSettingsQuery,
  useLazyGetFieldMappingTemplatesQuery,
  useMigrateLeadsToCrmMutation,
  type CrmStatus,
  type CrmSyncStatusEntry,
} from '@/store/features/crm.feature';
import {
  buildCrmIdentifierBatch,
  crmStatusIdentifierKey,
  type CrmIdentifierInput,
} from '@/utils/crm-status-identifier.util';

// ---------------------------------------------------------------------------
// Connected-CRM detection
// ---------------------------------------------------------------------------

/** The CRMs whose "in CRM" status the person surfaces show. */
export type CrmSyncCrmType = 'salesforce' | 'hubspot' | 'attio' | 'pipedrive';
/** The CRMs every other CRM surface supports (search CRM filters, plays,
 *  tasks, the companies directory). */
export type CoreSyncCrmType = Exclude<CrmSyncCrmType, 'attio' | 'pipedrive'>;

export interface ConnectedSyncCrm<T extends CrmSyncCrmType = CrmSyncCrmType> {
  type: T;
  name: string;
  logoUrl: string | null;
}

const CORE_SYNC_CRM_TYPES: readonly CoreSyncCrmType[] = [
  'salesforce',
  'hubspot',
];

const CRM_FALLBACK_NAMES: Record<CrmSyncCrmType, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  attio: 'Attio',
  pipedrive: 'Pipedrive',
};

const NO_CRMS: never[] = [];

function useOrgCrmOptions() {
  const { organization } = useOrganization();
  const organizationId = organization?.id ?? '';
  return useGetCrmOptionsQuery({ organizationId }, { skip: !organizationId })
    .currentData;
}

function connectedCrms<T extends CrmSyncCrmType>(
  options: ReturnType<typeof useOrgCrmOptions>,
  types: readonly T[]
): ConnectedSyncCrm<T>[] {
  if (!options?.length) return NO_CRMS;
  const connected: ConnectedSyncCrm<T>[] = [];
  for (const type of types) {
    // ``onboarded`` alone includes dropped connections (expired/revoked
    // token); every per-CRM endpoint 400s for those, so they don't count.
    const option = options.find(
      (o) => o.type === type && o.onboarded && o.is_connected
    );
    if (option) {
      connected.push({
        type,
        name: option.name || CRM_FALLBACK_NAMES[type],
        logoUrl: option.logo_url || null,
      });
    }
  }
  return connected.length > 0 ? connected : NO_CRMS;
}

/**
 * Connected CRMs (`onboarded && is_connected`), in stable order: Salesforce/HubSpot,
 * plus Attio with `withAttio` while `attio_crm_v1` is on (the person surfaces'
 * "in CRM" status; every other CRM surface only knows Salesforce/HubSpot),
 * plus Pipedrive with `withPipedrive` while `pipedrive_v1` is on (the
 * lead-list table: its status reads by lead id, which covers Pipedrive).
 * Returns a stable EMPTY array (same ref) when nothing is connected so
 * consumers can use the result directly in memo deps.
 */
export function useConnectedSyncCrms(): ConnectedSyncCrm<CoreSyncCrmType>[];
export function useConnectedSyncCrms(options: {
  withAttio?: true;
  withPipedrive?: true;
}): ConnectedSyncCrm[];
export function useConnectedSyncCrms(options?: {
  withAttio?: boolean;
  withPipedrive?: boolean;
}): ConnectedSyncCrm[] {
  const crmOptions = useOrgCrmOptions();
  const withAttio =
    useFeatureFlagEnabled('attio_crm_v1') === true &&
    Boolean(options?.withAttio);
  const withPipedrive =
    useFeatureFlagEnabled('pipedrive_v1') === true &&
    Boolean(options?.withPipedrive);
  return useMemo(
    () =>
      connectedCrms(crmOptions, [
        ...CORE_SYNC_CRM_TYPES,
        ...(withAttio ? (['attio'] as const) : []),
        ...(withPipedrive ? (['pipedrive'] as const) : []),
      ]),
    [crmOptions, withAttio, withPipedrive]
  );
}

/**
 * True once the CRM-options query has SETTLED (success OR error) — i.e.
 * `useConnectedSyncCrms` now reflects the real connection set rather than its
 * pre-fetch empty default. Lets callers avoid acting on a transient "no CRM
 * connected" before `/crm/options` resolves — e.g. firing a URL-hydrated
 * search before the active CRM is known, which silently drops CRM exclusions
 * on first load (B1). Degrades OPEN on error so a shared/bookmarked search
 * never hangs waiting on a failed options fetch.
 */
export function useCrmConnectionsReady(): boolean {
  const { organization } = useOrganization();
  const organizationId = organization?.id ?? '';
  const { currentData, isFetching, isError } = useGetCrmOptionsQuery(
    { organizationId },
    { skip: !organizationId }
  );
  return (
    Boolean(organizationId) &&
    !isFetching &&
    (currentData !== undefined || isError)
  );
}

// ---------------------------------------------------------------------------
// Status lookups
// ---------------------------------------------------------------------------

/** Returned when the BE answered but did not include the requested key. */
const NOT_SYNCED_ENTRY: CrmSyncStatusEntry = {
  state: 'not_synced',
  crm_record_id: null,
  last_at: null,
  exists_in_crm: null,
};

export interface UseCrmSyncStatusResult {
  /**
   * Entry for a record + CRM. `undefined` while loading or when the fetch
   * failed (render the dim "unknown" state); `null` when the BE reports
   * that CRM as not connected.
   */
  getEntry: (
    key: string,
    crm: CrmSyncCrmType
  ) => CrmSyncStatusEntry | null | undefined;
  /**
   * FOUR-STATE SSOT for the sync-status (lead + company) surfaces: true iff the
   * query is fetching AND `key` is in the CURRENT query's requested keys AND the
   * BE has NOT yet returned a record for it. Callers MUST gate on this BEFORE
   * `getEntry` so an omitted-but-in-flight key never collapses to the
   * `NOT_SYNCED_ENTRY` "Not in CRM" the instant the batch settles.
   */
  isResolving: (key: string) => boolean;
  isLoading: boolean;
  /** True when the status fetch failed and no data is available. */
  statusUnknown: boolean;
}

function useSortedUniqueKeys(
  keys: ReadonlyArray<string | null | undefined>,
  lowercase: boolean
): string[] {
  return useMemo(() => {
    const seen = new Set<string>();
    for (const key of keys) {
      if (!key) continue;
      seen.add(lowercase ? key.toLowerCase() : key);
    }
    return Array.from(seen).sort();
  }, [keys, lowercase]);
}

/** Batched lead sync-status lookup, keyed by lead UUID. */
export function useCrmSyncStatus(
  leadIds: ReadonlyArray<string | null | undefined>,
  options?: {
    skip?: boolean;
    /** Per-lead statuses already embedded in the leads payload (the BE now
     * serializes them into `lead.crm_sync_status`). Ids present here resolve
     * locally with zero fetch and zero spinner; only the gaps (e.g. rows
     * from an older cached page shape) go to the batch endpoint. */
    embedded?: ReadonlyMap<
      string,
      Partial<Record<CrmSyncCrmType, CrmSyncStatusEntry>>
    >;
  }
): UseCrmSyncStatusResult {
  const embedded = options?.embedded;
  const ids = useSortedUniqueKeys(leadIds, false);
  const missingIds = useMemo(
    () => (embedded ? ids.filter((id) => !embedded.has(id)) : ids),
    [ids, embedded]
  );
  const skip = Boolean(options?.skip) || missingIds.length === 0;
  const { data, isError, isLoading, isFetching } = useGetCrmSyncStatusQuery(
    { leadIds: missingIds },
    { skip }
  );

  const getEntry = useCallback(
    (key: string, crm: CrmSyncCrmType) => {
      const local = embedded?.get(key);
      if (local) return local[crm] ?? NOT_SYNCED_ENTRY;
      if (!data) return undefined;
      const record = data.statuses[key];
      if (!record) return NOT_SYNCED_ENTRY;
      return record[crm];
    },
    [data, embedded]
  );

  const idSet = useMemo(() => new Set(missingIds), [missingIds]);
  const isResolving = useCallback(
    (key: string) =>
      !embedded?.has(key) &&
      isFetching &&
      idSet.has(key) &&
      !data?.statuses[key],
    [isFetching, idSet, data, embedded]
  );

  return {
    getEntry,
    isResolving,
    isLoading,
    statusUnknown: isError && !data,
  };
}

/** Batched company sync-status lookup, keyed by lowercased domain. */
export function useCompanyCrmSyncStatus(
  domains: ReadonlyArray<string | null | undefined>,
  options?: { skip?: boolean }
): UseCrmSyncStatusResult {
  const keys = useSortedUniqueKeys(domains, true);
  const skip = Boolean(options?.skip) || keys.length === 0;
  const { data, isError, isLoading, isFetching } =
    useGetCrmCompanySyncStatusQuery({ domains: keys }, { skip });

  const getEntry = useCallback(
    (key: string, crm: CrmSyncCrmType) => {
      if (!data) return undefined;
      const record = data.statuses[key.toLowerCase()];
      if (!record) return NOT_SYNCED_ENTRY;
      return record[crm];
    },
    [data]
  );

  const keySet = useMemo(() => new Set(keys), [keys]);
  const isResolving = useCallback(
    (key: string) => {
      const k = key.toLowerCase();
      return isFetching && keySet.has(k) && !data?.statuses[k];
    },
    [isFetching, keySet, data]
  );

  return {
    getEntry,
    isResolving,
    isLoading,
    statusUnknown: isError && !data,
  };
}

// ---------------------------------------------------------------------------
// [FE-D] Rich per-lead CRM status (drives the hover/click popover)
// ---------------------------------------------------------------------------

export interface UseCrmStatusResult {
  /**
   * Rich status for a lead, or `undefined` while loading / when the fetch
   * failed / when the BE simply omitted the lead. A lead in `errored_lead_ids`
   * also returns `undefined` here — use `isErroredLead` to tell the two apart
   * (budget-errored = "status unavailable, retry"; truly absent = "not in
   * CRM").
   */
  getStatus: (leadId: string) => CrmStatus | undefined;
  /** True when the BE could not resolve this lead within its status budget. */
  isErroredLead: (leadId: string) => boolean;
  /**
   * FOUR-STATE SSOT (crm-status-no-perlead-resolution-bounded): the single
   * hook-owned per-lead "is this row still resolving?" predicate. True iff the
   * query is fetching AND this lead is in the CURRENT query's requested ids AND
   * it has NOT yet resolved (absent from statuses) AND it is NOT errored. The
   * "in the current query's ids" clause is load-bearing: the cache is
   * crm-keyed + delta-fetched + merged for 24h, so without it a never-fetched
   * lead from a prior page's id set is indistinguishable from a fetched-absent
   * true negative. EVERY fan-out surface MUST gate on this — never on the
   * batch-wide `isLoading` — so a still-in-flight lead can never render as a
   * settled "Not in CRM".
   */
  isResolving: (leadId: string) => boolean;
  isLoading: boolean;
  /** True when the status fetch failed and no data is available. */
  statusUnknown: boolean;
}

const EMPTY_ERRORED: ReadonlySet<string> = new Set();

/**
 * Batched rich-status lookup for one CRM, keyed by lead UUID. Mirrors
 * `useCrmSyncStatus`: it dedupes/sorts the id set so every surface shares one
 * cache entry per (crm, id set), and the RTK endpoint chunks the POST in small
 * batches (50) because this endpoint is heavier than sync-status.
 *
 * Callers pass the ACCUMULATED/visible page of lead ids on list-enter and on
 * paginate — NOT on hover. The endpoint is cache-first server-side and the
 * cache entry merges pages, so re-entry / scroll-back is free.
 */
export function useCrmStatus(
  leadIds: ReadonlyArray<string | null | undefined>,
  crm: CrmSyncCrmType | null | undefined,
  options?: { skip?: boolean }
): UseCrmStatusResult {
  const ids = useSortedUniqueKeys(leadIds, false);
  const skip = Boolean(options?.skip) || !crm || ids.length === 0;
  const { data, isError, isLoading, isFetching } = useGetCrmStatusQuery(
    { crm: crm ?? 'salesforce', leadIds: ids },
    { skip }
  );

  const erroredSet = useMemo(
    () => (data ? new Set(data.budget.errored_lead_ids) : EMPTY_ERRORED),
    [data]
  );

  const getStatus = useCallback(
    (leadId: string) => data?.statuses[leadId],
    [data]
  );

  const isErroredLead = useCallback(
    (leadId: string) => erroredSet.has(leadId),
    [erroredSet]
  );

  const idSet = useMemo(() => new Set(ids), [ids]);

  const isResolving = useCallback(
    (leadId: string) =>
      isFetching &&
      idSet.has(leadId) &&
      !data?.statuses[leadId] &&
      !erroredSet.has(leadId),
    [isFetching, idSet, data, erroredSet]
  );

  return {
    getStatus,
    isErroredLead,
    isResolving,
    // WF#5: a refetch/pagination (isFetching) is still RESOLVING — treat it as
    // loading, not a resolved "not in CRM". Kept for first-paint, but consumers
    // gate their per-row dim/skeleton on `isResolving` (the four-state SSOT).
    isLoading: isLoading || isFetching,
    statusUnknown: isError && !data,
  };
}

// ---------------------------------------------------------------------------
// [CP-11] Rich CRM status BY IDENTIFIER (people search + directory rows that
// are not yet Cleanlist leads — no lead_id). Same rich `CrmStatus` contract as
// `useCrmStatus`, but resolved by LinkedIn URL (+ optional email) so the SAME
// popover renders on every people surface.
// ---------------------------------------------------------------------------

export interface UseCrmStatusByIdentifierResult {
  /**
   * Rich status for a row, looked up by its LinkedIn URL. `undefined` while
   * loading, when the fetch failed / was AppSumo-fenced, when the URL doesn't
   * normalize to a key, or when the BE omitted the identifier. A key in
   * `errored_identifier_keys` also returns `undefined` here — use
   * `isErroredIdentifier` to tell budget-errored ("retry") from truly absent
   * ("not in CRM").
   */
  getStatus: (linkedinUrl: string | null | undefined) => CrmStatus | undefined;
  /** True when the BE could not resolve this identifier within its budget. */
  isErroredIdentifier: (linkedinUrl: string | null | undefined) => boolean;
  /**
   * FOUR-STATE SSOT (identifier analog of `useCrmStatus.isResolving`): true iff
   * the query is fetching AND this identifier key is in the CURRENT query's
   * requested identifiers AND it has NOT resolved AND it is NOT errored. The
   * "in the current query arg" clause disambiguates fetched-absent from
   * never-fetched on the crm-keyed merged cache. Every fan-out surface gates on
   * this, never on batch-wide `isLoading`.
   */
  isResolving: (linkedinUrl: string | null | undefined) => boolean;
  isLoading: boolean;
  /** True when the status fetch failed and no data is available. (The RTK
   *  endpoint swallows 403/errors into an empty success, so this is rarely
   *  true; callers should still hide the trigger when nothing resolves.) */
  statusUnknown: boolean;
}

const EMPTY_IDENTIFIER_RESULT: UseCrmStatusByIdentifierResult = {
  getStatus: () => undefined,
  isErroredIdentifier: () => false,
  isResolving: () => false,
  isLoading: false,
  statusUnknown: false,
};

/**
 * Batched rich-status lookup for one CRM, keyed by IDENTIFIER. Pass the
 * ACCUMULATED/visible page of rows (each carrying a `linkedinUrl` + optional
 * `email`) on page-enter / paginate — NOT on hover. Identifiers are deduped +
 * stable-sorted into ONE request per page; the endpoint is cache-first
 * server-side and the cache entry merges pages, so re-entry / scroll-back is
 * free and no request fires on every render or scroll.
 *
 * Gated on `useCrmConnectionsReady()` (CRM options settled) so a first-load
 * race doesn't fire before the active CRM is known, and skipped entirely when
 * `crm` is null (its CRM isn't connected) or there are zero usable identifiers.
 */
export function useCrmStatusByIdentifier(
  rows: ReadonlyArray<CrmIdentifierInput>,
  crm: CrmSyncCrmType | null | undefined,
  options?: { skip?: boolean }
): UseCrmStatusByIdentifierResult {
  const connectionsReady = useCrmConnectionsReady();

  // Dedupe + stable-sort into the canonical batch. Most callers pass a fresh
  // `rows` array each render, so we key the memo off a stable CONTENT hash of
  // the row set rather than its identity — the batch (and therefore the query
  // args) is recomputed only when the actual identifiers change, not on every
  // render/scroll. `buildCrmIdentifierBatch` is pure over `rows`, so reading it
  // inside the same memo keeps `rows` the only dependency (no stale closure).
  const rowsKey = rows
    .map((r) => `${r.linkedinUrl ?? ''}|${r.email ?? ''}`)
    .join('\n');
  const identifiers = useMemo(
    () => buildCrmIdentifierBatch(rows).identifiers,
    // rowsKey is the content hash of `rows`; `rows` itself changes identity
    // every render, so we intentionally key on the hash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsKey]
  );

  const skip =
    Boolean(options?.skip) ||
    !connectionsReady ||
    !crm ||
    identifiers.length === 0;

  const { data, isError, isLoading, isFetching } =
    useGetCrmStatusByIdentifierQuery(
      { crm: (crm ?? 'salesforce') as CrmSyncCrmType, identifiers },
      { skip }
    );

  const erroredSet = useMemo(
    () => (data ? new Set(data.budget.errored_identifier_keys) : EMPTY_ERRORED),
    [data]
  );

  const getStatus = useCallback(
    (linkedinUrl: string | null | undefined) => {
      if (!data) return undefined;
      const key = crmStatusIdentifierKey(linkedinUrl);
      if (!key) return undefined;
      return data.statuses[key];
    },
    [data]
  );

  const isErroredIdentifier = useCallback(
    (linkedinUrl: string | null | undefined) => {
      const key = crmStatusIdentifierKey(linkedinUrl);
      return key ? erroredSet.has(key) : false;
    },
    [erroredSet]
  );

  // Membership must be keyed by the canonical string key —
  // `buildCrmIdentifierBatch` already sets `linkedin_url` to that key.
  const identifierSet = useMemo(
    () => new Set(identifiers.map((id) => id.linkedin_url)),
    [identifiers]
  );

  const isResolving = useCallback(
    (linkedinUrl: string | null | undefined) => {
      const key = crmStatusIdentifierKey(linkedinUrl);
      if (!key) return false;
      return (
        isFetching &&
        identifierSet.has(key) &&
        !data?.statuses[key] &&
        !erroredSet.has(key)
      );
    },
    [isFetching, identifierSet, data, erroredSet]
  );

  if (skip) return EMPTY_IDENTIFIER_RESULT;

  return {
    getStatus,
    isErroredIdentifier,
    isResolving,
    // WF#5: a refetch/pagination (isFetching) is still RESOLVING — treat it as
    // loading, not a resolved "not in CRM" (this is the 305-row directory bug).
    // Kept for first-paint; per-row gating uses `isResolving` (four-state SSOT).
    isLoading: isLoading || isFetching,
    statusUnknown: isError && !data,
  };
}

// ---------------------------------------------------------------------------
// Single-lead click-to-sync (safe v2 push path)
// ---------------------------------------------------------------------------

/**
 * The CRM record object_type for the field-mapping-templates lookup. HubSpot
 * person sync is always `contact`; Salesforce can be `contact` or `lead`
 * depending on the org's create-contacts record_type.
 */
type SyncObjectType = 'contact' | 'lead';

export class CrmSyncMappingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CrmSyncMappingError';
  }
}

/**
 * Outcome of a single-lead push. A benign skip is NOT an error: pushLead
 * resolves with `{ outcome: 'skipped', reason }` instead of throwing, so the
 * caller can render a neutral chip rather than a red failure toast. Only a real
 * failure throws `CrmSyncMappingError`.
 */
export interface CrmPushLeadResult {
  outcome: 'done' | 'skipped';
  reason?: string;
}

export interface UseCrmSingleLeadSyncResult {
  /**
   * Push one lead to `crm`. Resolves the org's default field mapping +
   * record object_type first so migrate-leads takes the SAFE v2 push path
   * (omitting field_mapping silently routes to the LEGACY path). Throws
   * `CrmSyncMappingError` ONLY on a real failure (no default mapping, or a
   * failed_migrations>0 push); the caller surfaces a failure toast. A benign
   * skip resolves with `{ outcome: 'skipped', reason }` instead of throwing.
   *
   * @param refresh pass true to overwrite stale CRM values (preset=refresh).
   */
  pushLead: (args: {
    leadId: string;
    crm: CrmSyncCrmType;
    leadListId?: string | null;
    refresh?: boolean;
  }) => Promise<CrmPushLeadResult>;
  isSyncing: boolean;
}

/**
 * Encapsulates the single-lead click-to-sync flow shared by every surface's
 * sync cell. Lazily fetches the default field-mapping template + CRM settings
 * ONLY when a cell is actually clicked (zero subscriptions per rendered cell,
 * one shared cache entry per CRM), then calls migrate-leads with
 * `field_mapping` + `object_type` so the safe v2 push path is taken.
 */
export function useCrmSingleLeadSync(): UseCrmSingleLeadSyncResult {
  const { organization } = useOrganization();
  const organizationId = organization?.id ?? '';
  const [migrateLeads] = useMigrateLeadsToCrmMutation();
  const [fetchSettings] = useLazyGetCrmSettingsQuery();
  const [fetchTemplates] = useLazyGetFieldMappingTemplatesQuery();
  const [isSyncing, setIsSyncing] = useState(false);

  const pushLead = useCallback(
    async ({
      leadId,
      crm,
      leadListId,
      refresh,
    }: {
      leadId: string;
      crm: CrmSyncCrmType;
      leadListId?: string | null;
      refresh?: boolean;
    }): Promise<CrmPushLeadResult> => {
      setIsSyncing(true);
      try {
        // 1) Resolve the record object_type. Only Salesforce exposes a
        //    Contact-vs-Lead choice; HubSpot person sync is always contact.
        let objectType: SyncObjectType = 'contact';
        if (crm === 'salesforce') {
          const settings = await fetchSettings(
            { crmType: 'salesforce', organizationId },
            true // prefer cache
          ).unwrap();
          objectType =
            settings.create_contacts.record_type === 'Lead'
              ? 'lead'
              : 'contact';
        }

        // 2) Resolve the org's default mapping for (crm, object_type). Without
        //    field_mapping the BE silently takes the legacy push path, so we
        //    HARD-GATE on having a saved default template. Attio has no legacy
        //    path: with no template the push writes its built-in mapping.
        const templates = await fetchTemplates(
          { crmType: crm, objectType, organizationId },
          true // prefer cache
        ).unwrap();
        const defaultTemplate =
          templates.templates.find(
            (t) => t.id === templates.default_template_id
          ) ?? templates.templates.find((t) => t.is_default);
        const fieldMapping = defaultTemplate?.mappings.length
          ? defaultTemplate.mappings
          : undefined;
        // Attio and Pipedrive have no mapping settings yet: with no saved
        // default the backend writes its built-in mapping.
        if (!fieldMapping && crm !== 'attio' && crm !== 'pipedrive') {
          throw new CrmSyncMappingError('no-default-mapping');
        }

        // 3) Safe v2 push: field_mapping + object_type pin the safe path.
        const result = await migrateLeads({
          lead_ids: [leadId],
          connection_type: crm,
          field_mapping: fieldMapping,
          // migrate-leads validates object_type as a strict capitalized
          // Literal ("Contact" | "Lead"); objectType stays lowercase above for
          // the field-mapping-templates lookup (templates are keyed lowercase).
          // Sending the lowercase value here 422s the whole sync.
          object_type: objectType === 'lead' ? 'Lead' : 'Contact',
          ...(leadListId ? { lead_list_id: leadListId } : {}),
          // Re-sync of an already-lit cell is an idempotent upsert. `refresh` is
          // a DOCUMENTED EXPLICIT one-shot override: the user clicked "refresh"
          // to overwrite stale values for THIS cell, so we send preset:'refresh'
          // (explicit-arg-wins in the BE resolver). The non-refresh path omits
          // preset entirely so a normal sync inherits the org's persisted
          // write_preset column rather than forcing a per-call aggressiveness.
          ...(refresh ? { preset: 'refresh' } : {}),
          // CLE-423: this hook IS the manual per-row surface (popover Update/
          // Add, cell click) — the user explicitly asked to sync THIS record,
          // so lift the owned-record/open-deal guards. The backend only honors
          // it for a one-lead request from an org whose
          // manual_sync_guard_override_v1 flag is on (fail closed); bulk and
          // auto-push paths never send it.
          override_record_guards: true,
        }).unwrap();

        // A real failure (failed_migrations now EXCLUDES benign skips) throws
        // so the caller renders a failure toast.
        if (result.failed_migrations > 0) {
          const reason = result.results?.find(
            (r) => r.outcome === 'failed' || (!r.success && !r.reason)
          )?.error;
          throw new CrmSyncMappingError(reason || 'sync-failed');
        }
        // A pure skip (skipped>0, failed==0) is NOT a failure: resolve with a
        // neutral skipped signal carrying the reason so the caller can render a
        // neutral chip instead of a red toast.
        if ((result.skipped_migrations ?? 0) > 0) {
          const reason = result.results?.find(
            (r) => r.outcome === 'skipped'
          )?.reason;
          return { outcome: 'skipped', reason };
        }
        return { outcome: 'done' };
      } finally {
        setIsSyncing(false);
      }
    },
    [migrateLeads, fetchSettings, fetchTemplates, organizationId]
  );

  return { pushLead, isSyncing };
}
